import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Loader2, Home, RefreshCw, CreditCard } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const API_URL = process.env.REACT_APP_API_URL || '';
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

function CardForm({ orderCode, amount, customerName, onSuccess, onAlreadyPaid }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/payment/stripe/retry-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderCode }),
      });
      const data = await res.json();
      if (data.alreadyPaid) { onAlreadyPaid(); return; }
      if (!data.success) { setError(data.error || 'Error starting payment.'); return; }

      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(data.clientSecret, {
        payment_method: { card: elements.getElement(CardElement), billing_details: { name: customerName } },
      });
      if (stripeError) { setError(stripeError.message); return; }
      if (paymentIntent.status === 'succeeded') onSuccess();
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <label className="block text-sm font-semibold text-gray-700 mb-2">Card Details</label>
        <div className="px-3 py-3 border-2 border-gray-200 rounded-xl bg-white focus-within:border-pink-500 transition">
          <CardElement options={{ style: { base: { fontSize: '15px', color: '#374151', '::placeholder': { color: '#9ca3af' } } } }} />
        </div>
      </div>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">{error}</div>
      )}
      <button
        type="submit" disabled={submitting || !stripe}
        className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-pink-600 to-purple-600 text-white py-4 rounded-xl font-bold text-lg hover:shadow-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submitting
          ? <><Loader2 className="w-5 h-5 animate-spin" /> Processing...</>
          : <><CreditCard className="w-5 h-5" /> Pay €{amount?.toFixed(2)}</>
        }
      </button>
      <p className="text-center text-xs text-gray-400">🔒 Secured by Stripe</p>
    </form>
  );
}

export default function RetryPayment() {
  const { orderCode } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [alreadyPaid, setAlreadyPaid] = useState(false);
  const [cardSuccess, setCardSuccess] = useState(false);

  useEffect(() => {
    async function fetchOrder() {
      try {
        const res = await fetch(`${API_URL}/payment/order/${orderCode}`);
        const data = await res.json();
        if (data.alreadyPaid) {
          setAlreadyPaid(true);
        } else if (data.success) {
          setOrder(data.order);
        } else {
          setError(data.error || 'Order not found.');
        }
      } catch {
        setError('Connection error. Check your internet connection.');
      } finally {
        setLoading(false);
      }
    }
    fetchOrder();
  }, [orderCode]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-pink-600 animate-spin" />
      </div>
    );
  }

  if (alreadyPaid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-lg text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-9 h-9 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Order Already Paid</h1>
          <p className="text-gray-500 mb-6">This order has already been paid successfully. No action required.</p>
          <button onClick={() => navigate('/')} className="w-full bg-gradient-to-r from-pink-600 to-purple-600 text-white py-3 rounded-xl font-bold hover:shadow-lg transition-all">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-lg text-center">
          <p className="text-red-600 font-semibold mb-4">{error}</p>
          <button onClick={() => navigate('/')} className="w-full bg-gradient-to-r from-pink-600 to-purple-600 text-white py-3 rounded-xl font-bold">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (cardSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 py-10 px-4">
        <div className="max-w-md mx-auto space-y-4">
          <div className="text-center py-4">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-3">
              <CheckCircle className="w-9 h-9 text-green-600" />
            </div>
            <h1 className="text-2xl font-extrabold text-gray-900">Payment Confirmed!</h1>
            <p className="text-gray-500 text-sm mt-1">Order: <span className="font-bold text-pink-600">{orderCode}</span></p>
          </div>
          <div className="bg-green-50 rounded-2xl p-5 border border-green-200">
            <p className="text-sm text-green-800">You'll receive a confirmation email shortly. Your order is being processed.</p>
          </div>
          <button onClick={() => navigate('/')} className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-pink-600 to-purple-600 text-white py-3 rounded-xl font-bold hover:shadow-lg transition-all">
            <Home className="w-4 h-4" /> Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 py-10 px-4">
      <div className="max-w-md mx-auto space-y-4">

        <div className="text-center py-4">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-3">
            <RefreshCw className="w-9 h-9 text-orange-500" />
          </div>
          <h1 className="text-2xl font-extrabold text-gray-900">Complete Payment</h1>
          <p className="text-gray-500 text-sm mt-1">Order: <span className="font-bold text-pink-600">{orderCode}</span></p>
        </div>

        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm font-medium transition">
          ← Back
        </button>

        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-1">Amount Due</p>
          <p className="text-3xl font-extrabold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
            €{order.price.toFixed(2)}
          </p>
          <p className="text-sm text-gray-500 mt-1">Hello, <strong>{order.customerName}</strong></p>
        </div>

        <Elements stripe={stripePromise}>
          <CardForm
            orderCode={orderCode}
            amount={order.price}
            customerName={order.customerName}
            onSuccess={() => setCardSuccess(true)}
            onAlreadyPaid={() => setAlreadyPaid(true)}
          />
        </Elements>

      </div>
    </div>
  );
}
